import { StorageSerializers, useStorage } from '@vueuse/core'
import type { User } from '@/api/client'

// Запомненный на устройстве юзер (мультивход, GitHub-стиль): хватает для карточки
// на экране входа — кого показать и какими ключами логинить. credential_ids —
// passkey-ключи, привязанные на ЭТОМ устройстве (узнаём при passkey-логине/привязке),
// по ним карточка предлагает вход по ключу без ввода email.
export interface RememberedUser {
  id: number
  name: string | null
  email: string
  avatar_url: string | null
  credential_ids: string[]
}

const REMEMBERED_KEY = 'rememberedUsers'
const LEGACY_HINT_KEY = 'lastUser'

export function toRemembered(user: User, credentialIds: string[] = []): RememberedUser {
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    avatar_url: user.avatar_url,
    credential_ids: credentialIds,
  }
}

// Реактивный список запомненных юзеров поверх localStorage (образец — useStoredLocale):
// useStorage синкает между табами, переживает refresh и сам глотает ошибки private-mode.
// flush:'sync' — persist сразу при мутации, чтобы reload сразу после логина не гонялся с
// отложенной записью. При пустом списке мигрируем legacy одиночный hint `lastUser` (фаза 1).
export function useRememberedUsers() {
  const list = useStorage<RememberedUser[]>(REMEMBERED_KEY, [], undefined, { flush: 'sync' })
  if (list.value.length === 0) {
    const migrated = migrateLegacyHint()
    if (migrated) list.value = migrated
  }
  return list
}

// Одноразовая миграция старого одиночного hint `lastUser` в список — совместимость с
// устройствами, где hint лежал до мультивхода. Читаем через useStorage (JSON-парсинг +
// глушение битого значения из коробки), затем обнуляем ключ (write(null) → removeItem).
function migrateLegacyHint(): RememberedUser[] | null {
  const legacy = useStorage<User | null>(LEGACY_HINT_KEY, null, undefined, {
    flush: 'sync',
    writeDefaults: false,
    serializer: StorageSerializers.object,
    onError: () => {},
  })
  if (legacy.value === null) return null
  const migrated = [toRemembered(legacy.value)]
  legacy.value = null
  return migrated
}

// Upsert запомненного юзера. mergeCredentialId — passkey-ключ, использованный на
// этом устройстве (логин/привязка): добавляем без дублей к уже известным ключам.
// Запись поднимается в начало списка (последний вход — первым).
export function upsertRemembered(
  list: RememberedUser[],
  user: User,
  mergeCredentialId?: string,
): RememberedUser[] {
  const existing = list.find((u) => u.id === user.id)
  const credentialIds = existing ? [...existing.credential_ids] : []
  if (mergeCredentialId && !credentialIds.includes(mergeCredentialId)) {
    credentialIds.push(mergeCredentialId)
  }
  return [toRemembered(user, credentialIds), ...list.filter((u) => u.id !== user.id)]
}
